/**
 * Photos and clips on a recipe (Joe, 2026-09-23).
 *
 * ⭐ ONE FILE INPUT, NOT THREE. `accept="image/*,video/*"` with no `capture`
 * attribute is what makes a phone offer Take Photo / Photo Library / Browse in
 * its own sheet — camera, gallery and file picker from a single button, in the
 * words the phone already uses. Adding `capture` would FORCE the camera and
 * remove the other two, which is the opposite of what was asked for.
 *
 * ⛔ THE FILE GOES STRAIGHT TO R2. presign → PUT → confirm, so a long upload is
 * between the phone and the bucket rather than tying up the server that is also
 * trying to serve the plan.
 *
 * ⚠️ A row is only written after the PUT succeeds, so a dropped connection
 * leaves nothing behind. A gallery of broken images is worse than an upload
 * that plainly failed and can be retried.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { twoPlates } from "../api/twoPlates";

export function MealMedia({ mealId }: { mealId: number }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareNext, setShareNext] = useState(false);

  const q = useQuery({
    queryKey: ["meal-media", mealId],
    queryFn: () => twoPlates.mealMedia(mealId),
  });

  const remove = useMutation({
    mutationFn: (id: number) => twoPlates.deleteMedia(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["meal-media", mealId] }),
  });

  async function onPick(file: File) {
    setError(null);
    setBusy(true);
    try {
      // Ask first: the server checks type and size BEFORE anything uploads, so
      // a 400MB video is refused in a second rather than after four minutes.
      const { url, objectKey } = await twoPlates.presignMedia(mealId, {
        contentType: file.type,
        bytes: file.size,
      });

      const put = await fetch(url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);

      await twoPlates.confirmMedia(mealId, {
        objectKey,
        contentType: file.type,
        bytes: file.size,
        shared: shareNext,
      });
      await queryClient.invalidateQueries({ queryKey: ["meal-media", mealId] });
      setShareNext(false);
    } catch (e: any) {
      setError(e?.message || "That did not upload.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = ""; // so the same file can be retried
    }
  }

  // Storage is not set up — say nothing rather than offer a button that fails.
  if (q.data && !q.data.available) return null;

  const media = q.data?.media ?? [];
  const canShare = !!q.data?.canShare;

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Photos &amp; video</h2>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="border-line rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
        >
          {busy ? "Adding…" : "Add photo or video"}
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }}
      />

      {/* ⛔ Offered BEFORE the picker, not after the upload. A tick that appears
          once the photo is already in the bucket asks somebody to consent to
          something that has happened. Only on shared-library recipes, because a
          private recipe has nobody else cooking it. */}
      {canShare && (
        <label className="text-muted mt-2 flex items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={shareNext}
            onChange={(e) => setShareNext(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Share the next one with other households cooking this recipe
            <span className="block text-[11px] opacity-75">
              Off by default — a photo of your table stays in your kitchen unless you say otherwise.
            </span>
          </span>
        </label>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {media.length === 0 ? (
        <p className="text-muted mt-3 text-xs">
          Nothing yet. A photo of how it actually turned out is worth more than the recipe card.
        </p>
      ) : (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {media.map((m) => (
            <figure key={m.id} className="border-line relative overflow-hidden rounded-lg border">
              {m.kind === "video" ? (
                // controls + playsInline: an autoplaying video on a phone in a
                // kitchen is a jump scare, and iOS opens fullscreen without it.
                <video src={m.url} controls playsInline preload="metadata" className="aspect-square w-full object-cover" />
              ) : (
                <img src={m.url} alt={m.caption ?? "A photo of this meal"} loading="lazy" className="aspect-square w-full object-cover" />
              )}

              {/* Only your own kitchen's can be removed — seeing a shared photo
                  is not permission to delete it. */}
              {m.mine && (
                <button
                  type="button"
                  onClick={() => { if (confirm("Remove this?")) remove.mutate(m.id); }}
                  aria-label="Remove"
                  className="absolute right-1 top-1 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white"
                >
                  ×
                </button>
              )}
              {m.shared && (
                <figcaption className="text-muted px-2 py-1 text-[11px]">Shared</figcaption>
              )}
              {!m.mine && (
                <figcaption className="text-muted px-2 py-1 text-[11px]">From another kitchen</figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}
